import { DEFAULT_USDC_DECIMALS, ZERO_WEI } from '@kwenta/sdk/constants'
import { formatDollars, formatNumber, stripZeros, truncateNumbers } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { TransactionButton } from 'components/Button/Button'
import ErrorView from 'components/ErrorView'
import { InfoBoxCol } from 'components/InfoBox'
import NumericInput from 'components/Input/NumericInput'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import Loader from 'components/Loader'
import SegmentedControl from 'components/SegmentedControl'
import { selectV3DepositableBalances } from 'state/balances/selectors'
import { selectPendingOrdersCount, selectSubmittingFuturesTx } from 'state/futures/selectors'
import {
	approveCrossMarginDeposit,
	depositCMAccountKeeperBalance,
	withdrawCMAccountKeeperBalance,
} from 'state/futures/snxPerpsV3/actions'
import {
	selectDepositAllowances,
	selectKeeperUSDBalance,
	selectMarginEnginePermitted,
	selectRequiredKeeperUsdDeposit,
	selectRequiredUsdForPendingOrders,
} from 'state/futures/snxPerpsV3/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'

const ManageKeeperBalanceCrossMargin = memo(() => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const { USDC: depositableBalances } = useAppSelector(selectV3DepositableBalances)
	const isSubmitting = useAppSelector(selectSubmittingFuturesTx)
	const requiredDeposit = useAppSelector(selectRequiredKeeperUsdDeposit)
	const keeperBalance = useAppSelector(selectKeeperUSDBalance)
	const pendingOrdersCount = useAppSelector(selectPendingOrdersCount)
	const pendingOrdersCost = useAppSelector(selectRequiredUsdForPendingOrders)
	const marginEnginePermitted = useAppSelector(selectMarginEnginePermitted)
	const allowances = useAppSelector(selectDepositAllowances)

	const [amount, setAmount] = useState('')
	const [transferType, setTransferType] = useState(0)

	const requiresApproval = useMemo(() => {
		const spender = marginEnginePermitted ? 'marginEngine' : 'marketProxy'
		return allowances['USDC']?.[spender]?.lt(amount || 0)
	}, [allowances, amount, marginEnginePermitted])

	const withdrawableBalance = useMemo(
		() => (requiredDeposit.lte(0) ? keeperBalance.sub(pendingOrdersCost) : ZERO_WEI),
		[keeperBalance, pendingOrdersCost, requiredDeposit]
	)
	const maxBalance = useMemo(
		() => (transferType === 0 ? depositableBalances : withdrawableBalance),
		[transferType, depositableBalances, withdrawableBalance]
	)
	const maxBalanceDisplay = useMemo(
		() => stripZeros(truncateNumbers(maxBalance, DEFAULT_USDC_DECIMALS)),
		[maxBalance]
	)

	const onWithdrawKeeperDeposit = useCallback(() => {
		if (keeperBalance.eq(0)) return
		dispatch(
			withdrawCMAccountKeeperBalance(maxBalanceDisplay === amount ? keeperBalance : wei(amount))
		)
	}, [dispatch, amount, keeperBalance, maxBalanceDisplay])

	const onDepositKeeperDepositOrApprove = useCallback(async () => {
		if (requiresApproval) {
			dispatch(approveCrossMarginDeposit({}))
		} else {
			dispatch(depositCMAccountKeeperBalance(wei(amount)))
		}
	}, [requiresApproval, amount, dispatch])

	const exceedsLimit = useMemo(() => wei(amount || 0).gt(maxBalance), [amount, maxBalance])

	const isDisabled = useMemo(
		() => !amount || isSubmitting || exceedsLimit,
		[amount, isSubmitting, exceedsLimit]
	)

	const handleSetMax = useCallback(() => {
		setAmount(maxBalanceDisplay)
	}, [maxBalanceDisplay])

	const onChangeTab = (selection: number) => {
		setTransferType(selection)
	}

	return (
		<FlexDivCol rowGap="20px">
			<SectionSeparator />
			<InfoBoxContainer>
				<InfoBoxCol
					title={t('futures.modals.manage.order.total-balance')}
					textValue={formatDollars(keeperBalance, { suggestDecimals: true })}
				/>
				<InfoBoxCol
					title={t('futures.modals.manage.order.open-order-cost')}
					textValue={formatDollars(pendingOrdersCost, { suggestDecimals: true })}
				/>
				<InfoBoxCol
					title={t('futures.modals.manage.order.total-open-orders')}
					textValue={pendingOrdersCount.toString()}
				/>
				<InfoBoxCol
					title={t('futures.modals.manage.order.withdrawable')}
					textValue={formatDollars(withdrawableBalance, { suggestDecimals: true })}
				/>
			</InfoBoxContainer>
			<SegmentedControl
				values={['Deposit', 'Withdraw']}
				selectedIndex={transferType}
				onChange={onChangeTab}
			/>
			<FlexDivCol rowGap="10px">
				<BalanceContainer onClick={handleSetMax}>
					<BalanceText>{t('futures.market.trade.margin.modal.balance')}</BalanceText>
					<BalanceText>
						{t('futures.market.trade.margin.modal.balance')}:{' '}
						<span>
							{formatNumber(maxBalance, {
								suggestDecimals: true,
							})}
						</span>
					</BalanceText>
				</BalanceContainer>
				<StyledNumericInput
					dataTestId="futures-market-trade-keeper-margin-input"
					placeholder={maxBalanceDisplay}
					value={truncateNumbers(amount, DEFAULT_USDC_DECIMALS)}
					invalid={exceedsLimit}
					onChange={(_, v) => {
						setAmount(v)
					}}
					right={
						<MaxButton onClick={handleSetMax}>
							{t('futures.modals.manage.order.max-button')}
						</MaxButton>
					}
				/>
			</FlexDivCol>

			<TransactionButton
				data-testid="futures-market-trade-manage-keeper-button"
				disabled={isDisabled}
				fullWidth
				onClick={transferType === 0 ? onDepositKeeperDepositOrApprove : onWithdrawKeeperDeposit}
			>
				{isSubmitting ? (
					<Loader width="20px" height="20px" />
				) : (
					t(
						`futures.market.trade.orders.manage-keeper-deposit.${
							transferType === 0
								? requiresApproval
									? 'approve-and-deposit'
									: 'deposit'
								: 'withdraw'
						}`
					)
				)}
			</TransactionButton>
			{transferType === 1 && withdrawableBalance.lt(keeperBalance) && (
				<ErrorView
					messageType="warn"
					message={t('futures.modals.manage.order.cancel-all-warning')}
					containerStyle={{ marginBottom: '0px' }}
				/>
			)}
			{transferType === 0 && requiredDeposit.gt(0) && (
				<ErrorView
					messageType="warn"
					message={t('futures.modals.manage.order.insufficient-balance-error')}
					containerStyle={{ marginBottom: '0px' }}
				/>
			)}
			{transferType === 0 && requiredDeposit.eq(0) && keeperBalance.lt(5) && (
				<ErrorView
					messageType="warn"
					message={t('futures.modals.manage.order.low-balance-warning')}
					containerStyle={{ marginBottom: '0px' }}
				/>
			)}
		</FlexDivCol>
	)
})

export default ManageKeeperBalanceCrossMargin

export const BalanceContainer = styled(FlexDivRowCentered)`
	cursor: pointer;
	p {
		margin: 0;
	}
`

export const BalanceText = styled.p<{ $gold?: boolean }>`
	color: ${(props) =>
		props.$gold ? props.theme.colors.selectedTheme.yellow : props.theme.colors.selectedTheme.gray};
	span {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

const InfoBoxContainer = styled.div`
	box-sizing: border-box;
	width: 100%;
	border: ${(props) => props.theme.colors.selectedTheme.border};
	border-radius: 8px;
	padding: 15px;
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-template-rows: 1fr 1fr;
	grid-gap: 15px 15px;
`

const SectionSeparator = styled.div`
	height: 1px;
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
`

const MaxButton = styled.button`
	height: 25px;
	padding: 5px 10px;
	background: ${(props) => props.theme.colors.selectedTheme.button.background};
	border-radius: 95px;
	font-family: ${(props) => props.theme.fonts.regular};
	font-size: 11px;
	line-height: normal;
	border: ${(props) => props.theme.colors.selectedTheme.border};
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	cursor: pointer;
`

const StyledNumericInput = styled(NumericInput)`
	padding: 10px 15px;
	height: 100%;
	input {
		font-size: 15px;
		font-family: ${(props) => props.theme.fonts.mono};
		color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
		border: none;
		background-color: transparent;

		&:focus {
			outline: none;
		}
	}
`
